import { useEffect, useState, type FormEvent } from "react";
import { getUsersMe, patchMyPersonName } from "../auth/api-client";
import { isApiConfigured } from "../auth-config";
import { guessNameFromSchoolEmail } from "../people/schoolEmailNameGuess";
import { studentListLabel } from "../people/studentRosterLabel";

export function StudentNameCard({
  bearer,
  email,
}: {
  readonly bearer: string | null;
  readonly email: string;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedGiven, setSavedGiven] = useState<string | null>(null);
  const [savedFamily, setSavedFamily] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [guessed, setGuessed] = useState(false);

  useEffect(() => {
    if (!bearer || !isApiConfigured()) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const profile = await getUsersMe(bearer);
        if (cancelled) return;
        const given = profile.givenName?.trim() ?? "";
        const family = profile.familyName?.trim() ?? "";
        setSavedGiven(given || null);
        setSavedFamily(family || null);
        if (given && family) {
          setGivenName(given);
          setFamilyName(family);
          setGuessed(false);
          setEditing(false);
        } else {
          const guess = guessNameFromSchoolEmail(profile.email || email);
          setGivenName(guess?.givenName ?? "");
          setFamilyName(guess?.familyName ?? "");
          setGuessed(Boolean(guess));
          setEditing(true);
        }
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const guess = guessNameFromSchoolEmail(email);
        setGivenName(guess?.givenName ?? "");
        setFamilyName(guess?.familyName ?? "");
        setGuessed(Boolean(guess));
        setEditing(true);
        setError(e instanceof Error ? e.message : "Could not load your name");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, email]);

  if (!bearer || !isApiConfigured()) return null;

  const savedLabel =
    savedGiven && savedFamily
      ? studentListLabel({ email, givenName: savedGiven, familyName: savedFamily })
      : null;

  async function save(ev: FormEvent) {
    ev.preventDefault();
    if (!bearer) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await patchMyPersonName(bearer, { givenName, familyName });
      const given = saved.givenName?.trim() ?? "";
      const family = saved.familyName?.trim() ?? "";
      setSavedGiven(given || null);
      setSavedFamily(family || null);
      setGivenName(given);
      setFamilyName(family);
      setEditing(false);
      setGuessed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save your name");
    } finally {
      setSaving(false);
    }
  }

  function startEdit() {
    setGivenName(savedGiven ?? "");
    setFamilyName(savedFamily ?? "");
    setGuessed(false);
    setEditing(true);
    setError(null);
  }

  return (
    <section className="panel student-name-card">
      {loading ? (
        <p className="muted">Loading your name…</p>
      ) : savedLabel && !editing ? (
        <div className="student-name-card-saved">
          <div>
            <h2 className="panel-title">Your name</h2>
            <p className="muted" style={{ margin: "0.25rem 0 0" }}>
              Your teacher sees you as <strong>{savedLabel}</strong>.
            </p>
          </div>
          <button type="button" className="btn secondary tight" onClick={startEdit}>
            Edit
          </button>
        </div>
      ) : (
        <form className="stack-form tight" onSubmit={(ev) => void save(ev)}>
          <h2 className="panel-title">What should your teacher call you?</h2>
          {guessed ? (
            <p className="muted small" style={{ margin: 0 }}>
              We guessed this from your school email. Please fix it if it is wrong.
            </p>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              Your teacher uses this name to find you in the class list.
            </p>
          )}
          <div className="student-name-card-fields">
            <label className="field">
              <span className="field-label">First name</span>
              <input
                className="input"
                value={givenName}
                onChange={(ev) => setGivenName(ev.target.value)}
                autoComplete="given-name"
                disabled={saving}
                required
              />
            </label>
            <label className="field">
              <span className="field-label">Last name</span>
              <input
                className="input"
                value={familyName}
                onChange={(ev) => setFamilyName(ev.target.value)}
                autoComplete="family-name"
                disabled={saving}
                required
              />
            </label>
          </div>
          <div className="actions">
            {savedLabel ? (
              <button type="button" className="btn secondary" disabled={saving} onClick={() => setEditing(false)}>
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn primary" disabled={saving || !givenName.trim() || !familyName.trim()}>
              {saving ? "Saving…" : "Save name"}
            </button>
          </div>
        </form>
      )}
      {error ? (
        <p className="warn-banner" role="alert" style={{ marginTop: "0.75rem" }}>
          {error}
        </p>
      ) : null}
    </section>
  );
}
